/* ============================================
   QUOTE DASHBOARD — Customer Columns (Sold To / Ship To / Bill To)
   Asset: dashboard-customer-columns.js

   Extracted from dashboard-main.js on 2026-05-22 as Phase B.2.a of the
   dashboard refactor — first cut INTO buildDetailHtml itself. Loaded via
   <script defer> in sections/rep-quote-dashboard.liquid BEFORE main.js.

   Public API (attached to window.RQD):
     buildCustomerColumn(q, opts) -> html string
       opts.qIsPending — passed through verbatim from buildDetailHtml's
         local scope; preserves the pre-existing var-hoisting bug at
         L6870 where qIsPending is read BEFORE its `var qIsPending = ...`
         assignment at L6981, so the value is `undefined` at call time
         and the span-3 ternary always evaluates ''. PENDING rows
         therefore never actually receive the grid-column: span 3
         treatment the comment promises. NOT fixed in this extraction —
         preserving existing UI exactly. Flagged for a follow-up ship
         that either (a) hoists var qIsPending to top of buildDetailHtml
         or (b) computes it inside this module from q directly.

   Init contract (RQD.initCustomerColumns called once by main.js at boot):
     esc, formatDate, detailRow, fmtPhone, isResidentialOrg
     (all 5 are function declarations in main.js — safe to pass directly
     per the init-deps-var-hoisting-trap memo)
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  // Injected by RQD.initCustomerColumns (from main.js boot)
  var esc, formatDate, detailRow, fmtPhone, isResidentialOrg;

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — remembered Sold/Ship/Bill tab,
     keyed by deal reference. Written by RQD.rememberInfoTab (the tab click handler
     in dashboard-main.js), read on every render by buildCustomerColumn. In-memory
     only on purpose: a page reload collapses the detail row anyway, so there would
     be nothing left to restore. */
  var _openInfoTab = Object.create(null);

    // === buildCustomerColumn — extracted from main.js:6857-6978 ===
    // Sold To / Ship To / Bill To column with optional tab UI. Renders
    // the entire <div class="rqd-detail__card">...</div> block — caller
    // just concatenates the result into its own html accumulator.
    // Auto-corrected Organization row. The worker clears a phone number that
    // was misfiled as the org (checkout autofill) and flags it; render an
    // amber alert in the Organization slot so the rep knows it was scrubbed
    // and can fill in the real org. `original` is the raw phone we removed.
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-27) — a residential placeholder is a NOTE, not a
       company name, so it should not be dressed as one.

       Ryan, on RW-1339-A: *"can you make it read as 'n/a - residence' not 'N/a - Residence'"*.
       That Title Case is not how anyone typed it — the rep wrote "N/A - RESIDENCE" and the
       worker's all-caps normalizer title-cased it on the way through, which is exactly the
       treatment a real company name gets. The result reads like a firm called "N/a - Residence"
       instead of a note saying there is no organization and the goods go to a house.

       ⛔ LOWERCASED, NOT REWRITTEN. Canonicalising every placeholder to one string would throw
       away what the rep actually said — "n/a - home gym" and "n/a - residence" are not the same
       note — and Ryan asked for his own words back, just not shouting. Real org names are returned
       untouched: `isResidentialOrg` is the same predicate the Sold To pane already uses, so the
       three tabs cannot disagree about what counts as a placeholder.

       ⚠️ DISPLAY ONLY. Nothing is written back; the stored value stays exactly as typed, which is
       what the PDFs, the PO and the freight email all still read. */
    /* ⛔ THE TYPED TEXT IS NOT THROWN AWAY, it moves to the tooltip. Sold To has replaced this
       field with "Residence" since 2026-05-17 and shows nothing else, so a rep who wrote
       "n/a - home gym" or "n/a - residence (side door)" loses it with no way to get it back short
       of opening the Workstation. One canonical word on the card, the rep's own words on hover, is
       what makes collapsing the three panes onto one label safe rather than lossy.

       ⛔ RETURNS A ROW, NOT A STRING, and the tooltip rides on detailRow's own `title` argument.
       The first cut wrapped the value in `<span title=...>`, which `.rqd-detail__row
       span:first-child` then styled as a LABEL - `margin-right: 12px` and all - so "Residence"
       sat 12px off the right edge. That selector has no child combinator, so ANY span that is its
       parent's first child collects the label's styling. Keeping the value a bare text node is the
       fix; detailRow escapes the title itself. */
    function orgRow(val) {
      if (!isResidentialOrg(val)) return detailRow('Organization', val);
      var raw = String(val == null ? '' : val).trim();
      return detailRow('Organization', 'Residence', raw ? 'Typed as "' + raw + '"' : '');
    }

    function autoCorrectedOrgRow(original) {
      var tip = 'Auto-corrected: this looked like a phone number'
        + (original ? ' (' + original + ')' : '')
        + ', not an organization. Cleared so it won’t print on POs/quotes — verify the real org.';
      return detailRow('Organization',
        '<span class="rqd-address-autocorrected" title="' + esc(tip) + '">'
        + '⚠ Phone in org field — cleared</span>');
    }

    function buildCustomerColumn(q, opts) {
      opts = opts || {};
      var qIsPending = opts.qIsPending;  // see header comment re: pre-existing bug

      var html = '';
      // Column 1: Sold To card (with Ship To / Bill To tabs if they exist)
      var hasShipTo = !!q.shipTo;
      var hasBillTo = !!q.billTo;
      var hasTabs = hasShipTo || hasBillTo;
      var tabId = 'info-' + esc(q.reference).replace(/[^a-zA-Z0-9]/g, '_');

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — On PENDING rows
         the Column 2+3 area is intentionally empty (no Financials, no
         form-submission detail — the legacy notes textarea that used to
         live there migrated to the universal v2 Notes card below).
         Span the Sold To card across all 3 columns so the top row reads
         as a single full-width customer card instead of a narrow card +
         empty whitespace. */
      html += '<div class="rqd-detail__card"' + (qIsPending ? ' style="grid-column: span 3;"' : '') + '>';

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — pencil edit affordance on
         the Sold To card for ALL offline deals (leads, pending, quoted, sold…),
         not just pre-quote leads. The worker /lead-customer-update now patches
         whichever record owns the customer block (lead → pending → wm).
         CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-23) — online Shopify orders now
         ALSO get a pencil, wired to a different path: it's tab-aware and edits the
         Ship To pane only (handler routes Sold To / Bill To to an explanation —
         Sold To is Shopify-derived, billing is Admin-API-immutable). Saves the
         ship-to to Shopify via /online-update-shipping-address. Carries the order
         GID + name so the handler can hit that endpoint. */
      var editPencilBtn;
      if (q.source === 'online') {
        editPencilBtn = '<button type="button" class="rqd-edit-online-ship-pencil" data-ref="' + esc(q.reference) + '" data-order-gid="' + esc(q.id || '') + '" data-order-name="' + esc(q.orderName || q.reference) + '" title="Edit the open tab (Ship To address, or the customer email on Sold To)" aria-label="Edit the open tab" style="background:none;border:none;cursor:pointer;padding:2px 6px;margin-left:6px;color:var(--rq-text-secondary);font-size:13px;vertical-align:middle;line-height:1;">&#9998;</button>';
      } else {
        /* CUSTOM_SHIPTOPENCIL_BY_CLAUDE (Ryan 2026-10-02) — the click handler edits whichever tab is
           open (Sold To or Ship To), so the label no longer names one. */
        editPencilBtn = '<button type="button" class="rqd-edit-sold-pencil" data-edit-sold-ref="' + esc(q.reference) + '" title="Edit the open tab (Sold To or Ship To)" aria-label="Edit the open tab" style="background:none;border:none;cursor:pointer;padding:2px 6px;margin-left:6px;color:var(--rq-text-secondary);font-size:13px;vertical-align:middle;line-height:1;">&#9998;</button>';
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — REMEMBER the open info tab.
         The Sold/Ship/Bill selection used to live ONLY as a DOM class and every
         render hard-coded Sold To active, so ANY re-render silently snapped the
         card back to the first tab: the 2-minute background poll, and above all
         the immediate refetch that visibilitychange fires when the rep alt-tabs
         back from a carrier site. Ryan would copy half a ship-to, tab out to
         paste, come back, and copy the rest off the WRONG address. The selection
         now lives in _openInfoTab and is re-applied here, falling back to Sold To
         if the remembered pane no longer exists on this row. */
      var _wantTab = _openInfoTab[q.reference] || 'sold';
      if (_wantTab === 'ship' && !hasShipTo) _wantTab = 'sold';
      if (_wantTab === 'bill' && !hasBillTo) _wantTab = 'sold';
      function _tabBtn(pane, label) {
        return '<button class="rqd-info-tab' + (_wantTab === pane ? ' rqd-info-tab--active' : '') + '"'
          + ' data-tab="' + tabId + '-' + pane + '"'
          + ' data-info-ref="' + esc(q.reference) + '" data-info-pane="' + pane + '">' + label + '</button>';
      }
      function _paneCls(pane) {
        return 'rqd-info-pane' + (_wantTab === pane ? ' rqd-info-pane--active' : '');
      }

      if (hasTabs) {
        html += '<div class="rqd-info-tabs">';
        html += _tabBtn('sold', 'Sold To');
        if (hasShipTo) html += _tabBtn('ship', 'Ship To');
        if (hasBillTo) html += _tabBtn('bill', 'Bill To');
        html += editPencilBtn;
        html += '</div>';
      } else {
        html += '<div class="rqd-detail__section-title">Sold To' + editPencilBtn + '</div>';
      }

      // Sold To pane
      html += '<div class="' + _paneCls('sold') + '" data-pane="' + tabId + '-sold">';
      if (q.customerCompany) {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-17) — Residential
           submissions land with normalized strings like "N/a - Residence"
           (the all-caps normalizer title-cases "N/A - RESIDENCE" inputs).
           Render the clean label instead — matches the table cell's
           '–' treatment in spirit but explicit. */
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-27) — was its own inline ternary, which is how
           the three panes came to disagree: this one said "Residence", Ship To and Bill To printed
           the raw string. One helper now, so they cannot drift again — and Sold To gains the
           tooltip it never had. */
        html += orgRow(q.customerCompany);
      } else if (q.customerCompanyAutoCorrected) {
        html += autoCorrectedOrgRow(q.customerCompanyOriginal);
      }
      var soldStreet = [q.customerStreet, q.customerStreet2].filter(Boolean).join(', ');
      if (soldStreet) html += detailRow('Street', soldStreet);
      html += detailRow('City/State/Zip', q.location || '—');
      html += detailRow('Contact', q.customer);
      /* Ryan 2026-05-21: render email as a mailto link so reps can click
         to compose. Same pattern applied to Ship To + Bill To below. */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — "+ Person" Phase 2: a deal can carry
         SECONDARY contacts, and this row used to print the whole comma-joined string into ONE
         mailto href. Two addresses made one dead link, which is the same shape as the composer
         bug that turned two chips into one red pill: a field that learned to hold several
         values, read by code that still assumed one.

         The primary keeps the Email row exactly as it was, so a one-contact deal (almost all
         of them) is visually unchanged. Extra people get their own row, each with a WORKING
         mailto, and a contact recorded with only a name and a phone still shows -- it just
         isn't a mail link. Falls back to the old single-string render if RepPortal.Contacts
         hasn't loaded; a missing global must never blank the customer card. */
      var _CT = window.RepPortal && window.RepPortal.Contacts;
      if (_CT && (q.customerEmail || (q.customerContacts && q.customerContacts.length))) {
        var _people = _CT.readContacts({
          name: q.customer, email: q.customerEmail,
          phone: q.customerPhone, contacts: q.customerContacts
        });
        var _primary = _people[0];
        var _extras = _people.slice(1);
        if (_primary && _primary.email) {
          html += detailRow('Email', '<a href="mailto:' + esc(_primary.email) + '">' + esc(_primary.email) + '</a>');
        }
        if (_extras.length) {
          var _personRow = function (c) {
            var bits = [];
            if (c.name) bits.push('<span class="rqd-cc-name">' + esc(c.name) + '</span>');
            if (c.email) bits.push('<a href="mailto:' + esc(c.email) + '">' + esc(c.email) + '</a>');
            if (c.phone) bits.push('<a href="tel:' + esc(c.phone) + '">' + esc(fmtPhone(c.phone)) + '</a>');
            return bits.join(' <span class="rqd-cc-sep">&middot;</span> ');
          };
          /* CUSTOM_DEALPEOPLE_BY_CLAUDE (Ryan 2026-10-27) - each extra person is To or CC on every email now, so the card says
             which: "Also To" beside the main contact, "CC" for the copied. A person with no address can never be emailed and
             stays under "Also". */
          var _list = function (arr) { return '<div class="rqd-cc-list">' + arr.map(_personRow).join('</div><div class="rqd-cc-list">') + '</div>'; };
          var _alsoTo = _extras.filter(function (c) { return c.email && c.role === 'to'; });
          var _ccd = _extras.filter(function (c) { return c.email && c.role !== 'to'; });
          var _noAddr = _extras.filter(function (c) { return !c.email; });
          if (_alsoTo.length) html += detailRow('Also To', _list(_alsoTo));
          if (_ccd.length) html += detailRow('CC', _list(_ccd));
          if (_noAddr.length) html += detailRow('Also', _list(_noAddr));
        }
      } else if (q.customerEmail) {
        html += detailRow('Email', '<a href="mailto:' + esc(q.customerEmail) + '">' + esc(q.customerEmail) + '</a>');
      }
      if (q.customerPhone) html += detailRow('Phone', fmtPhone(q.customerPhone));
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — referral SOURCE (BLOCK B):
         who referred the deal (Lead Source = Referral), tracking only, no payout.
         Shown for relationship tracking (year-end gift list / future "all
         referrers" report). Independent of a Shared Deal recipient (which is
         paid and shows on the Commission card). */
      if (q.referralSourceName) {
        html += detailRow('Referred By', esc(q.referralSourceName) + ' <span style="font-size:11px;color:var(--rq-text-secondary);">(tracking)</span>');
      }
      if (q.lifecycleStatus === 'sold') {
        html += detailRow('Date Sold', q.dateSold ? formatDate(q.dateSold) : '—');
        var sb = q.soldBasis;
        if (sb) {
          var basisParts = [];
          var autoSignedFromPayment = sb.paidOnline === true || sb.source === 'external' || sb.source === 'online-invoice';
          if (sb.signedOrder && !autoSignedFromPayment) {
            basisParts.push('Signed Order &#10003;');
          }
          if (sb.poReceived) basisParts.push('PO' + (sb.poNumber ? ' #' + esc(sb.poNumber) : '') + ' &#10003;');
          if (sb.paymentReceived) basisParts.push((sb.paymentMethod || 'Payment') + (sb.paymentAmount ? ' &mdash; $' + esc(sb.paymentAmount) : '') + ' &#10003;');
          if (basisParts.length) html += detailRow('Sold Basis', basisParts.join(' &nbsp;|&nbsp; '));
          if (sb.source === 'external' && sb.externalOrderName) {
            var extLink = sb.externalOrderUrl
              ? '<a class="rqd-ext-link" href="' + esc(sb.externalOrderUrl) + '" target="_blank" rel="noopener">' + esc(sb.externalOrderName) + '</a>'
              : esc(sb.externalOrderName);
            /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08 PM⁴) — the pill's green was
               hardcoded INLINE, which no stylesheet can override, so it stayed green on the
               navy linked card. Moved to .rqd-ext-order-pill (dashboard.css) — same green by
               default, repainted pink inside .rqd-detail--linked. */
            html += detailRow('External Order', extLink + ' <span class="rqd-ext-order-pill">Linked</span>');
          }
        }
        var tw = q.tncWaiver;
        if (tw && tw.active) {
          html += detailRow('T&C Waiver', esc(tw.reason || 'Active') + ' <span style="font-size:11px;color:#92400e;background:#fef3c7;padding:2px 6px;border-radius:4px;margin-left:6px;">Waiver</span>');
        }
      }
      if (q.lifecycleStatus === 'lost') {
        html += detailRow('Date Lost', q.dateLost ? formatDate(q.dateLost) : '—');
        html += detailRow('Reason', q.lostReason || '—');
      }
      html += '</div>';

      // Ship To pane
      if (hasShipTo) {
        html += '<div class="' + _paneCls('ship') + '" data-pane="' + tabId + '-ship">';
        if (q.shipTo.company) html += orgRow(q.shipTo.company);
        else if (q.shipTo.companyAutoCorrected) html += autoCorrectedOrgRow(q.shipTo.companyOriginal);
        var shipStreet = [q.shipTo.street, q.shipTo.street2].filter(Boolean).join(', ');
        if (shipStreet) html += detailRow('Street', shipStreet);
        var shipLoc = [q.shipTo.city, q.shipTo.state].filter(Boolean).join(', ');
        if (q.shipTo.zip) shipLoc = shipLoc ? shipLoc + ' ' + q.shipTo.zip : q.shipTo.zip;
        if (shipLoc) html += detailRow('City/State/Zip', shipLoc);
        if (q.shipTo.name) html += detailRow('Contact', q.shipTo.name);
        if (q.shipTo.email) html += detailRow('Email', '<a href="mailto:' + esc(q.shipTo.email) + '">' + esc(q.shipTo.email) + '</a>');
        if (q.shipTo.phone) html += detailRow('Phone', fmtPhone(q.shipTo.phone));
        html += '</div>';
      }

      // Bill To pane
      if (hasBillTo) {
        html += '<div class="' + _paneCls('bill') + '" data-pane="' + tabId + '-bill">';
        /* Bill To gets the same treatment as Ship To above. Ryan named Ship To, but both panes
           render the raw stored string and the same placeholder appears in both — fixing one would
           leave the other shouting on the same card. */
        if (q.billTo.company) html += orgRow(q.billTo.company);
        else if (q.billTo.companyAutoCorrected) html += autoCorrectedOrgRow(q.billTo.companyOriginal);
        var billStreet = [q.billTo.street, q.billTo.street2].filter(Boolean).join(', ');
        if (billStreet) html += detailRow('Street', billStreet);
        var billLoc = [q.billTo.city, q.billTo.state].filter(Boolean).join(', ');
        if (q.billTo.zip) billLoc = billLoc ? billLoc + ' ' + q.billTo.zip : q.billTo.zip;
        if (billLoc) html += detailRow('City/State/Zip', billLoc);
        if (q.billTo.name) html += detailRow('Contact', q.billTo.name);
        if (q.billTo.email) html += detailRow('Email', '<a href="mailto:' + esc(q.billTo.email) + '">' + esc(q.billTo.email) + '</a>');
        if (q.billTo.phone) html += detailRow('Phone', fmtPhone(q.billTo.phone));
        html += '</div>';
      }

      html += '</div>';
      return html;
    }

  // === Init contract ===
  window.RQD.initCustomerColumns = function(d) {
    d = d || {};
    esc              = d.esc;
    formatDate       = d.formatDate;
    detailRow        = d.detailRow;
    fmtPhone         = d.fmtPhone;
    isResidentialOrg = d.isResidentialOrg;
  };

  // === Public API ===
  window.RQD.buildCustomerColumn = buildCustomerColumn;
  /* Record which info tab the rep just opened on a row, so the next render
     reopens it instead of resetting to Sold To. See _openInfoTab above. */
  window.RQD.rememberInfoTab = function (ref, pane) {
    if (ref && (pane === 'sold' || pane === 'ship' || pane === 'bill')) _openInfoTab[ref] = pane;
  };
})();
